import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, Download, Sparkles, Search } from "lucide-react";
import SiteSearch from "@/components/SiteSearch";
import AccountMenu from "@/components/AccountMenu";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useInstallPrompt } from "@/lib/pwa";
import { useBilling } from "@/context/BillingContext";
import { toast } from "sonner";

const NAV_LINKS = [
  { to: "/", key: "home" },
  { to: "/learn", key: "learn" },
  { to: "/guides", key: "guides" },
  { to: "/certification", key: "certification" },
  { to: "/quiz", key: "quiz" },
  { to: "/simulator", key: "simulator" },
  { to: "/workspace", key: "workspace" },
  { to: "/pricing", key: "pricing" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); setSearchOpen((o) => !o); } };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);
  const installer = useInstallPrompt();
  const billing = useBilling();
  const showUpgrade = !!user && !billing.loading && !billing.access;
  const onInstallApp = async () => {
    setOpen(false);
    if (installer.canPrompt) { await installer.install(); return; }
    toast(t("pwa.installTitle"), { description: `${t("pwa.iosStep1")} → ${t("pwa.iosStep2")}`, duration: 8000 });
  };

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  // Subscribers / staff don't need Pricing in the bar (it stays in the footer and mobile menu).
  const desktopLinks = NAV_LINKS.filter((l) => l.key !== "pricing" || !user || !billing.access);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#0B132B]/70 border-b border-white/10">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group min-h-[44px] shrink-0 mr-2">
          <img src="/logo-firealarmbt-light.png" alt="FireAlarm BT — Fire Alarm Blueprint Trainer" width="702" height="209" fetchPriority="high" decoding="async"
            className="h-8 w-auto shrink-0 transition-transform duration-200 group-hover:scale-[1.03]" />
        </Link>

        <nav className="hidden xl:flex items-center gap-1 min-w-0 overflow-x-auto hide-scrollbar">
          {desktopLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.key}`}
              className={`px-3 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors duration-200 ${
                isActive(l.to) ? "text-white bg-white/10" : "text-slate-300 hover:text-white hover:bg-white/5"
              }`}
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-2 shrink-0 ml-2">
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="nav-search-btn" aria-label={t("search.title")} title={`${t("search.title")} (Ctrl/⌘ K)`}
            className="grid place-items-center w-10 h-10 rounded-full text-slate-300 hover:text-white hover:bg-white/10 transition-colors"><Search size={18} /></button>
          <LanguageSwitcher />
          {user ? (
            <>
              {showUpgrade && (
                <Button size="sm" data-testid="nav-upgrade-btn" onClick={() => navigate("/pricing")}
                  className="rounded-full bg-amber-400 text-[#0B132B] hover:bg-amber-300 font-semibold">
                  <Sparkles size={15} className="mr-1.5" /> {t("nav.upgrade")}
                </Button>
              )}
              <AccountMenu user={user} billing={billing} onLogout={() => { logout(); navigate("/"); }} />
            </>
          ) : (
            <>
              <Button variant="ghost" size="sm" data-testid="nav-login-btn" onClick={() => navigate("/login")}
                className="text-slate-200 hover:bg-white/10 hover:text-white">{t("nav.signIn")}</Button>
              <Button size="sm" data-testid="nav-signup-btn" onClick={() => navigate("/register")}
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-4">{t("nav.getStarted")}</Button>
            </>
          )}
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <button type="button" onClick={() => setSearchOpen(true)} data-testid="nav-search-btn-mobile" aria-label={t("search.title")}
            className="grid place-items-center min-w-[44px] min-h-[44px] rounded-md text-white active:bg-white/10"><Search size={22} /></button>
          <LanguageSwitcher />
          <button className="grid place-items-center min-w-[44px] min-h-[44px] -mr-2.5 rounded-md text-white active:bg-white/10" data-testid="nav-mobile-toggle" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-white/10 bg-[#0B132B] px-4 py-3 space-y-1 max-h-[calc(100dvh-4rem)] overflow-y-auto" data-testid="nav-mobile-menu">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="flex items-center min-h-[44px] px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t(`nav.${l.key}`)}</Link>
          ))}
          <div className="pt-2 border-t border-white/10 flex flex-col gap-2">
            {installer.available && (
              <button type="button" data-testid="nav-install-app" onClick={onInstallApp} className="flex items-center gap-2 min-h-[44px] text-left px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10"><Download size={18} className="text-primary" /> {t("pwa.installMenu")}</button>
            )}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t("nav.dashboard")}</Link>
                {showUpgrade && (
                  <Link to="/pricing" data-testid="nav-upgrade-link" onClick={() => setOpen(false)} className="flex items-center gap-2 min-h-[44px] px-3 rounded-md text-base font-semibold text-amber-300 hover:bg-amber-400/10"><Sparkles size={16} /> {t("nav.upgrade")}</Link>
                )}
                <Link to="/profile" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t("nav.profile")}</Link>
                {(user.role === "instructor" || user.role === "administrator") && (
                  <Link to="/instructor" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-primary hover:bg-primary/10 active:bg-primary/10">{t("nav.instructor")}</Link>
                )}
                {(billing.plan === "company" || billing.source === "role") && (
                  <Link to="/inspections" onClick={() => setOpen(false)} data-testid="nav-mobile-inspections" className="flex items-center min-h-[44px] px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t("nav.inspections", "Inspections")}</Link>
                )}
                {billing.team_access && (
                  <Link to="/team" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-primary hover:bg-primary/10 active:bg-primary/10">{t("nav.team")}</Link>
                )}
                {user.role === "administrator" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-primary hover:bg-primary/10 active:bg-primary/10">{t("nav.admin")}</Link>
                )}
                <button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="flex items-center min-h-[44px] text-left px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t("nav.signOut")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="flex items-center min-h-[44px] px-3 rounded-md text-base text-slate-200 hover:bg-white/10 active:bg-white/10">{t("nav.signIn")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="flex items-center justify-center min-h-[44px] px-3 rounded-md text-base font-semibold bg-primary text-white text-center">{t("nav.getStarted")}</Link>
              </>
            )}
          </div>
        </div>
      )}
      <SiteSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </header>
  );
}
